/**
 * ai-chat Stimulus controller — authenticated Chip AI floating widget.
 *
 * Drives templates/components/ai_chat_widget.html: a launcher button that
 * opens a slide-up panel (the same .pchat-* chrome as the public widget) over
 * POST /chat/stream (Server-Sent Events).
 *
 * The streaming engine is unchanged from the former static/js/ai_chat.js:
 * in-memory message history per page session (no localStorage), the full
 * message array POSTed each turn (the server is stateless), a live-stream
 * typewriter pump that smooths text_delta jitter, tool-call cards, write-
 * approval prompts, and the subscription
 * upgrade prompt. It is wrapped in an engine factory so the class
 * methods (open/close/send/...) and the rendered approval buttons (routed via
 * global-actions.js data-fn -> window.* below) share one instance's state.
 *
 * data-turbo-permanent on the widget root keeps this controller's connect()
 * from running more than once per full page load, so the transcript persists
 * across Turbo navigations.
 *
 * Wires templates/components/ai_chat_widget.html.
 */
import { Controller } from "@hotwired/stimulus";
import { autosizeTextarea } from "./_autosize.js";
import { attachSwipeDismiss, cancelClose, closeSheet, isClosing, playOnce } from "./_sheet_motion.js";
import { pinToVisibleArea, unpinVisibleArea } from "./_visible_area.js";

// The width below which the open widget is a full-screen sheet (public_chat.css).
const PHONE_SHEET = '(max-width: 767px)';

/**
 * Whether a Turbo visit puts the open widget away. Only the phone sheet
 * covers the page, so it closes to show the page the user went to; the
 * desktop panel floats beside the page and stays open. page-check's silent
 * reload of the current page is a visit too, and must not close it.
 * Closing only hides the panel: the transcript survives on reopen.
 */
export function closesOnVisit({ open, phoneSheet, pageCheckSwap }) {
    return open && phoneSheet && !pageCheckSwap;
}

/**
 * The "[label](/path)" links left in already-escaped Chip markdown once entity
 * and help-page links are drawn: a link when the path is an app page Chip may
 * link (support_links.LINK_PAGES, passed as escaped paths), the bare label
 * otherwise, so a path the model made up never renders as a dead link or as raw
 * Markdown. The path shape is support_links.PAGE_LINK's: one leading slash.
 */
export function linkAppPages(html, allowedPaths) {
    return html.replace(/\[([^\]\n]+)\]\((\/(?![/\\])[^\s)\\]*)\)/g, (_match, label, href) =>
        allowedPaths.has(href) ? `<a href="${href}" class="chat-entity-link">${label}</a>` : label);
}

export default class extends Controller {
        static values = { linkPages: { type: Array, default: [] } };

        static targets = [
            'panel', 'launcher', 'messages', 'intro',
            'input', 'send', 'spinner', 'sendicon'
        ];

        // data-turbo-permanent: Turbo moves this element into every new page,
        // and Stimulus disconnects and reconnects it each time. The engine (and
        // with it the transcript) is built once; the listeners go with every
        // disconnect, so every connect adds them back.
        connect() {
            if (!this._engine) this._initialise();
            this._listen();
        }

        _initialise() {
            this._engine = createEngine(this);
            // Approval / decline buttons inside rendered messages route
            // through global-actions.js `data-fn` — expose those handlers globally,
            // bound to this instance's engine. The Send button and New-chat use
            // Stimulus actions directly (see template), but resetChat/sendChatMessage
            // stay exposed too for parity with the prior global API.
            window.sendChatMessage = () => this._engine.sendChatMessage();
            window.resetChat = () => this._engine.resetChat();
            window.approveWrite = (id) => this._engine.approveWrite(id);
            window.declineWrite = (id) => this._engine.declineWrite(id);
            window.askFounders = () => this._engine.askFounders();
        }

        _listen() {
            this._onKeydown = (e) => {
                if (e.key === 'Escape' && this.element.classList.contains('pchat--open')) {
                    this.close();
                }
            };
            document.addEventListener('keydown', this._onKeydown);
            this._onViewport = () => this._syncViewport();
            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', this._onViewport);
                window.visualViewport.addEventListener('scroll', this._onViewport);
            }
            this._detachSwipe = attachSwipeDismiss(this.panelTarget,
                () => window.matchMedia(PHONE_SHEET).matches, () => this._closeNow());
        }

        disconnect() {
            if (this._detachSwipe) this._detachSwipe();
            if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
            if (this._onViewport && window.visualViewport) {
                window.visualViewport.removeEventListener('resize', this._onViewport);
                window.visualViewport.removeEventListener('scroll', this._onViewport);
            }
        }

        // The on-screen keyboard shrinks the visible area. The open phone sheet
        // is pinned to that area (_visible_area.js) so the header stays on
        // screen, and unpinned when closed or wider than a phone. The CSS
        // bottom-anchors whichever block is visible (intro or transcript), so
        // short content already hugs the input row. visualViewport fires
        // resize/scroll on every keyboard show/hide; scroll to the real bottom —
        // the last message, or the "talk to a human" button in the intro — so
        // the keyboard never covers it when content OVERFLOWS.
        // A sheet on its way out keeps its size: the keyboard dropping during the
        // close must not stretch it mid-slide (_closeNow unpins it).
        _syncViewport() {
            if (isClosing(this.panelTarget)) return;
            const phoneSheet = this.element.classList.contains('pchat--open') &&
                window.matchMedia(PHONE_SHEET).matches;
            if (!phoneSheet) {
                unpinVisibleArea(this.element);
                return;
            }
            pinToVisibleArea(this.element);
            const body = this.element.querySelector('.pchat-body');
            if (body) body.scrollTop = body.scrollHeight;
        }

        toggle() {
            if (this.element.classList.contains('pchat--open')) this.close();
            else this.open();
        }

        open() {
            cancelClose(this.panelTarget);
            this.element.classList.add('pchat--open');
            if (this.hasPanelTarget) this.panelTarget.hidden = false;
            if (this.hasLauncherTarget) this.launcherTarget.setAttribute('aria-expanded', 'true');
            this._syncViewport();
            this._engine.focusInput();
        }

        // The shrink plays first (_sheet_motion.js); _closeNow is the real close.
        // The keyboard drops as the shrink starts, not after the panel has gone,
        // when the page would jump back under it.
        close() {
            if (this.panelTarget.contains(document.activeElement)) document.activeElement.blur();
            closeSheet(this.panelTarget, () => this._closeNow());
        }

        _closeNow() {
            const wasOpen = this.element.classList.contains('pchat--open');
            this.element.classList.remove('pchat--open');
            if (this.hasPanelTarget) this.panelTarget.hidden = true;
            if (this.hasLauncherTarget) this.launcherTarget.setAttribute('aria-expanded', 'false');
            // The launcher pops back only as the panel closes into it, not each
            // time it reappears after a keyboard or modal hid it.
            if (wasOpen && this.hasLauncherTarget) playOnce(this.launcherTarget, 'pchat-launcher--pop', 400);
            this._syncViewport();
        }

        // turbo:visit@document (ai_chat_widget.html).
        closeOnVisit() {
            const pageCheck = window.chippiePageCheck;
            if (closesOnVisit({
                open: this.element.classList.contains('pchat--open'),
                phoneSheet: window.matchMedia(PHONE_SHEET).matches,
                pageCheckSwap: !!(pageCheck && pageCheck.isSwapping()),
            })) this.close();
        }

        newChat() { this._engine.resetChat(); }
        send() { this._engine.sendChatMessage(); }
        askChip(e) { this._engine.askChip((e.currentTarget.textContent || '').trim()); }

        // Typing and dictation (which dispatches input) both land here.
        autosize() {
            if (this.hasInputTarget) autosizeTextarea(this.inputTarget);
        }

        onInputKeydown(e) {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this._engine.sendChatMessage();
            }
        }
}

    // ----------------------------------------------------------------------
    // Streaming engine. One per controller instance; element access goes
    // through the controller's targets rather than document.getElementById.
    // ----------------------------------------------------------------------
    function createEngine(ctrl) {
        const CHAT_URL = '/chat/stream';
        // Shown when the user sends while approval cards wait for an answer.
        const ANSWER_CARDS_FIRST = { message: 'Approve or cancel the action above first, then send your message.' };
        const messages = [];
        const errors = [];     // pseudo-messages routed through renderMessages so they survive re-renders
        const liveTools = [];  // [{id, name, args, status: 'running' | 'done', deferred?}] populated while streaming
        let pendingWrites = [];
        const writeDecisions = new Map();  // call id -> 'approve' | 'decline', for cards in pendingWrites
        let history = '';      // opaque base64 blob of pydantic-ai message history; server-stateless round-trip
        // One transcript session id per page load, in memory only (no localStorage)
        // — fresh on hard refresh and in a new tab, stable across Turbo nav. Sent
        // on every /chat/stream POST so the server can group captured turns;
        // treated as opaque server-side. Null on browsers without crypto.randomUUID.
        const SESSION_ID = (window.crypto && window.crypto.randomUUID)
            ? window.crypto.randomUUID()
            : null;
        let streamingText = '';  // chars already typed out on screen (revealed by the pump)
        let inFlight = false;

        // Live-stream typewriter pump. The model streams text_delta in phrase/line-
        // sized chunks, which reads as janky lurches. Deltas feed streamQueue and a
        // single rAF pump drains it into streamingText at STREAM_CPS, so the answer
        // types out smoothly however the network chunks it. STREAM_MAX_LAG_MS bounds
        // the catch-up: if the backlog would trail the live stream by more than this,
        // the pump speeds up so it never falls further behind.
        const STREAM_CPS = 120;
        const STREAM_MAX_LAG_MS = 120;
        let streamQueue = '';      // chars arrived but not yet typed out
        let streamRAF = null;
        let streamCredit = 0;      // fractional-character accumulator across frames
        let streamLast = 0;        // performance.now() of the previous tick
        let pendingDone = null;    // parked done frame, applied once the queue drains
        let streamResolve = null;  // lets postChat() await the typewriter

        // Stick-to-bottom. While the user is within SCROLL_PIN_PX of the bottom, new
        // content auto-scrolls to follow; once they scroll up to re-read, we leave
        // their position alone. forceScroll overrides it for the turn the user sends.
        const SCROLL_PIN_PX = 48;
        let forceScroll = false;

        // Post-`done` typewriter. Reveals the most-recent assistant text reply
        // char-by-char for turns that arrive entirely in the done frame with no
        // text_delta. typingMsgIdx points at the message in `messages`;
        // typingShown is how many chars are visible so far.
        const TYPING_CHARS_PER_TICK = 8;
        const TYPING_TICK_MS = 8;
        let typingMsgIdx = -1;
        let typingShown = 0;
        let typingTimer = null;
        // The most-recent assistant content we've already finished animating.
        // Suppresses re-animation when a turn produces no fresh assistant text
        // (e.g. an approval-deferred write) — without this the typewriter walks
        // back to the prior turn's reply and retypes it from char 0.
        let lastShownAssistantContent = '';

        // Render coalescing. Streaming deltas arrive in rapid bursts and the
        // typewriter fallback ticks every 8ms — both far faster than the display
        // refreshes. Funnelling every visual update through a single rAF collapses
        // a burst into at most one repaint per frame (~60fps).
        let pendingFrame = null;
        function coalesceRender(fn) {
            if (pendingFrame !== null) return;
            pendingFrame = requestAnimationFrame(() => {
                pendingFrame = null;
                fn();
            });
        }
        function cancelPendingRender() {
            if (pendingFrame !== null) {
                cancelAnimationFrame(pendingFrame);
                pendingFrame = null;
            }
        }

        function messagesBox() {
            return ctrl.hasMessagesTarget ? ctrl.messagesTarget : null;
        }

        // .pchat-body (public_chat.css) owns overflow-y, NOT #aiChatMessages —
        // the content node inside it has no scrollbar. Pin-detection and
        // stick-to-bottom must target that parent or they're silent no-ops.
        function scrollerBox() {
            const box = messagesBox();
            return box ? box.parentElement : null;
        }

        // isPinned() must be read BEFORE any DOM mutation, since after it
        // scrollHeight jumps while scrollTop holds — only the pre-mutation
        // reading reflects whether the user was following the bottom.
        function isPinned(scroller) {
            if (!scroller) return true;
            return scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= SCROLL_PIN_PX;
        }
        function stickBottom(scroller) {
            if (scroller) scroller.scrollTop = scroller.scrollHeight;
        }

        // Cheap update path for streaming text growth: touch only the single live
        // assistant bubble instead of rebuilding the whole panel per delta.
        function updateStreamingBubble() {
            const box = messagesBox();
            if (!box) return;
            const el = box.querySelector('.chat-msg--streaming');
            if (!el) {
                renderMessages();  // bubble not in DOM yet (first delta) — full render
                return;
            }
            const scroller = scrollerBox();
            const stick = forceScroll || isPinned(scroller);
            el.innerHTML = renderAssistantMarkdown(streamingText);
            if (stick) stickBottom(scroller);
            forceScroll = false;
        }

        // --- live-stream typewriter pump ------------------------------------

        function startStreamTyping() {
            if (streamRAF !== null) return;
            streamLast = performance.now();
            const tick = (now) => {
                streamRAF = null;
                const dt = now - streamLast;
                streamLast = now;
                if (streamQueue.length) {
                    let cps = STREAM_CPS;
                    const lagMs = (streamQueue.length / cps) * 1000;
                    if (lagMs > STREAM_MAX_LAG_MS) {
                        cps = streamQueue.length / (STREAM_MAX_LAG_MS / 1000);
                    }
                    streamCredit += (dt / 1000) * cps;
                    const n = Math.floor(streamCredit);
                    if (n > 0) {
                        streamCredit -= n;
                        streamingText += streamQueue.slice(0, n);
                        streamQueue = streamQueue.slice(n);
                        updateStreamingBubble();
                    }
                }
                if (streamQueue.length) {
                    streamRAF = requestAnimationFrame(tick);
                } else if (pendingDone) {
                    finishStreamDone();
                } else {
                    streamCredit = 0;
                    resolveStreamTyping();
                }
            };
            streamRAF = requestAnimationFrame(tick);
        }

        function stopStreamTyping() {
            if (streamRAF !== null) {
                cancelAnimationFrame(streamRAF);
                streamRAF = null;
            }
            streamCredit = 0;
            resolveStreamTyping();
        }

        // Resolves once the pump is idle — the queue is drained and any pending
        // done frame finalized. postChat() awaits this before tearing down.
        function awaitStreamTyping() {
            if (streamRAF === null && !streamQueue.length && !pendingDone) {
                return Promise.resolve();
            }
            return new Promise(resolve => { streamResolve = resolve; });
        }

        function resolveStreamTyping() {
            if (streamResolve) {
                const resolve = streamResolve;
                streamResolve = null;
                resolve();
            }
        }

        // Apply the parked done frame once the live text has fully typed out.
        function finishStreamDone() {
            const data = pendingDone;
            pendingDone = null;
            cancelPendingRender();
            cancelTyping(true);
            errors.length = 0;
            liveTools.length = 0;
            const hadStreamedText = streamingText.length > 0;
            streamingText = '';
            streamQueue = '';
            messages.length = 0;
            messages.push(...(data.messages || []));
            pendingWrites = data.pending_writes || [];
            writeDecisions.clear();
            history = data.history || '';
            if (hadStreamedText) {
                for (let i = messages.length - 1; i >= 0; i--) {
                    const m = messages[i];
                    if (m.role === 'assistant' && m.content) {
                        lastShownAssistantContent = m.content;
                        break;
                    }
                }
            }
            startTypingLastAssistant();
            renderMessages();
            resolveStreamTyping();
        }

        function cancelTyping(finalize) {
            if (typingTimer) {
                clearInterval(typingTimer);
                typingTimer = null;
            }
            if (finalize && typingMsgIdx >= 0 && messages[typingMsgIdx]) {
                typingShown = (messages[typingMsgIdx].content || '').length;
            }
            typingMsgIdx = -1;
            typingShown = 0;
        }

        function startTypingLastAssistant() {
            for (let i = messages.length - 1; i >= 0; i--) {
                const m = messages[i];
                if (m.role === 'assistant' && m.content) {
                    if (m.content === lastShownAssistantContent) {
                        typingMsgIdx = -1;
                        typingShown = 0;
                        return;
                    }
                    typingMsgIdx = i;
                    typingShown = 0;
                    typingTimer = setInterval(() => {
                        typingShown += TYPING_CHARS_PER_TICK;
                        if (typingShown >= m.content.length) {
                            typingShown = m.content.length;
                            lastShownAssistantContent = m.content;
                            cancelPendingRender();
                            renderMessages();
                            cancelTyping(false);
                        } else {
                            coalesceRender(renderMessages);
                        }
                    }, TYPING_TICK_MS);
                    return;
                }
            }
        }

        // App Store 3.1.1 / Google Play Payments: no purchase surface inside the
        // native shells. The 402 card keeps saying WHY the feature is locked and
        // drops the "upgrade" wording and the /billing/plans link, matching the
        // server-rendered gates (the seat cap on templates/team/invite_member.html
        // and friends). Those read Jinja's is_native_shell; markup built here has
        // no server context, so read the twin flag base.html stamps on <html>
        // (the same test maps-navigation.js and pwa-install.js use).
        function isNativeShell() {
            return document.documentElement.hasAttribute('data-hotwire-native');
        }

        function escapeHtml(text) {
            if (text == null) return '';
            return String(text)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        }

        // The app pages' paths escaped the way renderAssistantMarkdown escapes
        // the reply, so a path compares equal to its own text in the reply.
        let appPagePathSet = null;
        function appPagePaths() {
            if (!appPagePathSet) {
                appPagePathSet = new Set(ctrl.linkPagesValue.map(page => escapeHtml(page.path)));
            }
            return appPagePathSet;
        }

        // Minimal safe markdown renderer for assistant replies. HTML-escape first,
        // then transform a small subset. Output is safe because all user/model
        // input is escaped before any markup is injected.
        function renderAssistantMarkdown(text) {
            text = text.replace(/(\S)(\*\*[^*\n]+:\*\*)/g, '$1\n$2');
            let html = escapeHtml(text);
            html = html.replace(/```([\s\S]*?)```/g, (_, code) =>
                `<pre class="chat-code"><code>${code}</code></pre>`);
            html = html.replace(/`([^`\n]+)`/g, '<code>$1</code>');
            html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
            html = html.replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s.,;:!?)]|$)/g, '$1<em>$2</em>');
            // Markdown entity links — "[Robin Smith](/client/240)" — the PRIMARY
            // link form Chip is instructed to emit. The href is constrained to our
            // four internal detail paths, so no model-controlled URL can slip
            // through. The label is already-escaped text.
            html = html.replace(
                /\[([^\]\n]+)\]\((\/(?:job|quote|invoice|client)\/\d+)\)/gi,
                (_match, label, href) =>
                    `<a href="${href}" class="chat-entity-link">${label}</a>`
            );
            // Help-page links — "[Taking payments](/docs/taking-payments)" — the
            // public help pages the help tools return. Constrained to /docs and a
            // lowercase slug, the shape PublicDocsService accepts. The hash carries
            // the page the reader is on, so the help page can show a "Back to
            // Chippie" button to it (docs_return_controller.js).
            const fromPage = encodeURIComponent(window.location.pathname + window.location.search);
            html = html.replace(
                /\[([^\]\n]+)\]\((\/docs(?:\/[a-z0-9][a-z0-9-]*)?)\)/g,
                (_match, label, href) =>
                    `<a href="${href}#from=${fromPage}" class="chat-entity-link">${label}</a>`
            );
            // App pages — "[Settings → Invoicing](/settings?tab=invoicing)" —
            // same tab, so Turbo keeps the user in the app (and the phone sheet
            // closes on the visit).
            html = linkAppPages(html, appPagePaths());
            // "[Ask the founders](#support)" — the hand-off the prompt asks for
            // when no help page covers a question.
            html = html.replace(/\[([^\]\n]+)\]\(#support\)/g, (_match, label) =>
                `<button type="button" class="btn btn-sm btn-outline-primary" ` +
                `data-action="call-fn" data-fn="askFounders">` +
                `<i class="bi bi-chat-dots me-1"></i>${label}</button>`);
            // Fallback linkifier for bare entity references — "Job 46", "invoice
            // #12" — runs after escapeHtml so matched text is already safe.
            html = html.replace(
                /\b(job|quote|invoice|client)(s)?\s*#?\s*(\d+)\b/gi,
                (match, kind, _plural, id) =>
                    `<a href="/${kind.toLowerCase()}/${id}" class="chat-entity-link">${match}</a>`
            );
            const lines = html.split('\n');
            const out = [];
            let listType = null;  // null | 'ul' | 'ol'
            let textBuffer = [];
            const flushText = () => {
                if (textBuffer.length) {
                    out.push(`<p class="chat-md-p">${textBuffer.join('<br>')}</p>`);
                    textBuffer = [];
                }
            };
            const closeList = () => {
                if (listType) { out.push(`</${listType}>`); listType = null; }
            };
            const openList = (kind) => {
                if (listType && listType !== kind) closeList();
                if (!listType) {
                    out.push(kind === 'ol' ? '<ol class="chat-md-list">' : '<ul class="chat-md-list">');
                    listType = kind;
                }
            };
            const orderedRe = /^[\s ]*(\d+)[.)][\s ]+(.*)$/;
            const unorderedRe = /^[\s ]*[-*][\s ]+(.*)$/;
            const headingRe = /^[\s ]*(#{1,3})[\s ]+(.*)$/;
            let orderedSeq = 0;
            lines.forEach(line => {
                const heading = line.match(headingRe);
                const ordered = line.match(orderedRe);
                const unordered = line.match(unorderedRe);
                if (heading) {
                    flushText();
                    closeList();
                    orderedSeq = 0;
                    const level = Math.min(3, heading[1].length) + 2;  // # -> h3, ## -> h4, ### -> h5
                    out.push(`<h${level} class="chat-md-heading">${heading[2]}</h${level}>`);
                } else if (ordered) {
                    flushText();
                    if (listType !== 'ol') orderedSeq = 0;
                    openList('ol');
                    orderedSeq += 1;
                    out.push(`<li class="chat-md-step"><span class="chat-md-num">${orderedSeq}.</span> ${ordered[2]}</li>`);
                } else if (unordered) {
                    flushText();
                    openList('ul');
                    out.push(`<li>${unordered[1]}</li>`);
                } else if (line.trim() === '') {
                    flushText();
                } else {
                    flushText();
                    closeList();
                    orderedSeq = 0;
                    textBuffer.push(line);
                }
            });
            flushText();
            closeList();
            return out.join('');
        }

        const TOOL_VERBS = {
            list_jobs: 'Reading jobs',
            get_job: 'Reading job',
            get_client: 'Reading client',
            list_clients: 'Reading clients',
            list_unpaid_invoices: 'Reading unpaid invoices',
            list_outstanding_invoices: 'Reading outstanding invoices',
            get_revenue_this_month: 'Calculating monthly revenue',
            add_job_note: 'Adding note to job',
            list_help_topics: 'Browsing help topics',
            get_help_topic: 'Reading help topic',
            get_setup_status: 'Checking your setup',
            get_dashboard_stats: 'Reading business stats',
            get_dashboard_data: 'Loading dashboard overview',
            get_recent_activity: 'Reading recent activity',
            mark_invoice_paid: 'Marking invoice paid',
            mark_payment_submitted: 'Recording payment submitted',
            confirm_payment: 'Confirming payment',
            reject_payment: 'Rejecting payment',
            send_invoice_email: 'Emailing invoice',
            create_client: 'Adding client',
            update_client: 'Updating client',
            create_quote: 'Drafting quote',
            list_today_events: "Reading today's calendar",
            list_upcoming_events: 'Reading upcoming events',
            schedule_quote_visit: 'Scheduling quote visit',
            schedule_job_work: 'Scheduling job work',
            reschedule_event: 'Rescheduling event',
            cancel_event: 'Cancelling event',
            complete_event: 'Completing event'
        };

        function verbForTool(name) {
            return TOOL_VERBS[name] || `Calling ${name}`;
        }

        function formatArgs(args) {
            if (!args || typeof args !== 'object') return '';
            const argStr = Object.entries(args)
                .map(([k, v]) => `${k}=${typeof v === 'string' ? `"${v}"` : v}`)
                .join(', ');
            return argStr ? ` (${argStr})` : '';
        }

        function describeToolCall(tc) {
            const name = tc.function && tc.function.name;
            let parsed = {};
            try { parsed = JSON.parse((tc.function && tc.function.arguments) || '{}'); }
            catch (_) { /* ignore */ }
            return `${verbForTool(name)}${formatArgs(parsed)}`;
        }

        function renderLiveTool(t) {
            const label = `${verbForTool(t.name)}${formatArgs(t.args)}`;
            const running = t.status === 'running';
            const cls = running ? 'chat-tool--running' : 'chat-tool--done';
            const icon = running
                ? `<span class="chat-tool__spinner" aria-hidden="true"></span>`
                : `<i class="bi bi-check-circle-fill chat-tool__check"></i>`;
            return `<div class="chat-msg chat-msg--tool ${cls}" data-call-id="${escapeHtml(t.id)}">` +
                `${icon}<span class="chat-tool__label">${escapeHtml(label)}</span>` +
                (t.deferred ? `<span class="chat-tool__deferred">awaiting approval</span>` : '') +
                `</div>`;
        }

        function renderMessages(showTyping) {
            const box = messagesBox();
            if (!box) return;
            // Intro (greeting + suggestion chips) is shown only on an empty
            // transcript; once anything is rendered it gets out of the way.
            const hasContent = messages.length || streamingText ||
                liveTools.length || errors.length || pendingWrites.length;
            if (ctrl.hasIntroTarget) ctrl.introTarget.style.display = hasContent ? 'none' : '';
            const parts = [];
            // Tool calls baked into completed assistant messages are tracked so we
            // can suppress liveTools already "absorbed" into the final payload.
            const finalisedToolIds = new Set();
            messages.forEach(m => {
                if (m.role === 'assistant' && m.tool_calls) {
                    m.tool_calls.forEach(tc => { if (tc.id) finalisedToolIds.add(tc.id); });
                }
            });
            messages.forEach((m, idx) => {
                if (m.role === 'user' && m.content) {
                    parts.push(`<div class="chat-msg chat-msg--user">${escapeHtml(m.content)}</div>`);
                } else if (m.role === 'assistant') {
                    if (m.tool_calls && m.tool_calls.length) {
                        m.tool_calls.forEach(tc => {
                            parts.push(
                                `<div class="chat-msg chat-msg--tool chat-tool--done" data-call-id="${escapeHtml(tc.id || '')}">` +
                                `<i class="bi bi-check-circle-fill chat-tool__check"></i>` +
                                `<span class="chat-tool__label">${escapeHtml(describeToolCall(tc))}</span></div>`
                            );
                        });
                    }
                    if (m.content) {
                        const visible = idx === typingMsgIdx
                            ? m.content.slice(0, typingShown)
                            : m.content;
                        parts.push(`<div class="chat-msg chat-msg--assistant">${renderAssistantMarkdown(visible)}</div>`);
                    }
                }
            });
            liveTools.forEach(t => {
                if (finalisedToolIds.has(t.id)) return;
                parts.push(renderLiveTool(t));
            });
            if (streamingText) {
                parts.push(
                    `<div class="chat-msg chat-msg--assistant chat-msg--streaming">` +
                    renderAssistantMarkdown(streamingText) +
                    `</div>`
                );
            }
            if (showTyping) {
                parts.push(
                    `<div class="chat-typing" aria-label="Assistant is typing">` +
                    `<span></span><span></span><span></span></div>`
                );
            }
            errors.forEach(err => {
                if (err === ANSWER_CARDS_FIRST) {
                    parts.push(`<div class="chat-msg small text-muted">${escapeHtml(err.message)}</div>`);
                } else if (err && err.type === 'upgrade') {
                    parts.push(
                        `<div class="chat-msg chat-upgrade-prompt">` +
                        `<div class="chat-upgrade-prompt__title">` +
                        `<i class="bi bi-stars me-1"></i>Premium feature</div>` +
                        `<div class="chat-upgrade-prompt__body">Chip AI is included with an active subscription.` +
                        (isNativeShell() ? `</div>` :
                            ` Upgrade to unlock AI chat.</div>` +
                            `<a href="/billing/plans" class="btn btn-sm btn-primary mt-2">` +
                            `<i class="bi bi-arrow-up-right-circle me-1"></i>View plans</a>`) +
                        `</div>`
                    );
                } else if (err && err.type === 'demo_cap') {
                    // The demo's daily turns are used: the way on is naming the
                    // business, the same form the demo banner and walkthrough open.
                    parts.push(
                        `<div class="chat-msg small">${escapeHtml(err.message)}` +
                        `<div><button type="button" class="btn btn-sm btn-primary mt-2" ` +
                        `data-open-modal="businessSetupModal">Name your business</button></div></div>`
                    );
                } else if (err && err.type === 'support') {
                    // A failed run: the server sends a fixed message, never the
                    // exception, and asks for a way to reach the founders.
                    parts.push(
                        `<div class="chat-msg small">${escapeHtml(err.message)}` +
                        `<div><button type="button" class="btn btn-sm btn-outline-primary mt-2" ` +
                        `data-action="call-fn" data-fn="openSupport">` +
                        `<i class="bi bi-chat-dots me-1"></i>Message the founders</button></div></div>`
                    );
                } else {
                    parts.push(`<div class="chat-msg text-danger small">${escapeHtml(String(err))}</div>`);
                }
            });
            pendingWrites.forEach(pw => {
                const decided = writeDecisions.get(pw.id);
                if (decided) {
                    parts.push(
                        `<div class="chat-msg chat-write-prompt" data-call-id="${escapeHtml(pw.id)}">` +
                        `<div class="chat-write-prompt__body">${escapeHtml(pw.summary)}</div>` +
                        `<div class="small text-muted">${decided === 'approve' ? 'Approved' : 'Cancelled'}. ` +
                        `Answer the other actions to carry on.</div></div>`
                    );
                    return;
                }
                parts.push(
                    `<div class="chat-msg chat-write-prompt" data-call-id="${escapeHtml(pw.id)}">` +
                    `<div class="chat-write-prompt__title"><i class="bi bi-shield-exclamation me-1"></i>Confirm action</div>` +
                    `<div class="chat-write-prompt__body">${escapeHtml(pw.summary)}</div>` +
                    `<div class="chat-write-prompt__actions">` +
                      `<button type="button" class="btn btn-sm btn-success" ` +
                        `data-action="call-fn" data-fn="approveWrite" data-entity-id="${escapeHtml(pw.id)}">` +
                        `<i class="bi bi-check-lg me-1"></i>Approve</button>` +
                      `<button type="button" class="btn btn-sm btn-outline-secondary" ` +
                        `data-action="call-fn" data-fn="declineWrite" data-entity-id="${escapeHtml(pw.id)}">` +
                        `Cancel</button>` +
                    `</div></div>`
                );
            });
            const scroller = scrollerBox();
            const stick = forceScroll || isPinned(scroller);
            box.innerHTML = parts.join('');
            if (stick) stickBottom(scroller);
            forceScroll = false;
        }

        function setSpinner(on) {
            if (ctrl.hasSpinnerTarget) ctrl.spinnerTarget.hidden = !on;
            if (ctrl.hasSendiconTarget) ctrl.sendiconTarget.hidden = on;
            if (ctrl.hasSendTarget) ctrl.sendTarget.disabled = on;
        }

        function handleSseEvent(event, data) {
            if (event === 'tool_start') {
                liveTools.push({
                    id: data.id,
                    name: data.name,
                    args: data.args || {},
                    status: 'running',
                    deferred: !!data.deferred,
                });
                renderMessages();
            } else if (event === 'tool_end') {
                const t = liveTools.find(x => x.id === data.id);
                if (t) {
                    t.status = 'done';
                    if (data.deferred) t.deferred = true;
                    renderMessages();
                }
            } else if (event === 'text_delta') {
                streamQueue += data.chunk || '';
                startStreamTyping();
            } else if (event === 'pending_writes') {
                // Reserved for future incremental delivery; the done frame carries
                // the authoritative list.
            } else if (event === 'done') {
                pendingDone = data;
                startStreamTyping();
            } else if (event === 'error') {
                stopStreamTyping();
                streamQueue = '';
                pendingDone = null;
                cancelPendingRender();
                liveTools.length = 0;
                streamingText = '';
                pendingWrites = [];
                errors.push(data.support
                    ? { type: 'support', message: data.message }
                    : `Error: ${data.message || 'Request failed'}`);
                renderMessages();
            }
        }

        async function consumeSseStream(resp) {
            // Minimal SSE parser. Frames separated by a blank line; within a frame
            // each line is `field: value`. Multi-line `data` concatenated per spec.
            const reader = resp.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let sep;
                while ((sep = buffer.indexOf('\n\n')) !== -1) {
                    const frame = buffer.slice(0, sep);
                    buffer = buffer.slice(sep + 2);
                    let event = 'message';
                    const dataLines = [];
                    frame.split('\n').forEach(line => {
                        if (line.startsWith('event:')) event = line.slice(6).trim();
                        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
                    });
                    if (!dataLines.length) continue;
                    let parsed;
                    try { parsed = JSON.parse(dataLines.join('\n')); }
                    catch (_) { continue; }
                    handleSseEvent(event, parsed);
                }
            }
        }

        async function postChat(payload) {
            if (inFlight) return;
            inFlight = true;
            setSpinner(true);
            liveTools.length = 0;
            streamingText = '';
            streamQueue = '';
            pendingDone = null;
            forceScroll = true;
            renderMessages(true);
            try {
                const body = Object.assign({}, payload);
                if (SESSION_ID) body.session_id = SESSION_ID;
                const resp = await fetch(CHAT_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
                    credentials: 'same-origin',
                    body: JSON.stringify(body)
                });
                if (resp.status === 402) {
                    cancelTyping(true);
                    liveTools.length = 0;
                    pendingWrites = [];
                    errors.push({ type: 'upgrade' });
                    renderMessages();
                    return;
                }
                if (!resp.ok || !resp.body) {
                    let detail = 'Request failed';
                    let data = null;
                    try {
                        data = await resp.json();
                        detail = (data && (data.message || data.error)) || detail;
                    } catch (_) { /* non-JSON body */ }
                    liveTools.length = 0;
                    pendingWrites = [];
                    errors.push(data && data.code === 'demo_turn_cap'
                        ? { type: 'demo_cap', message: detail }
                        : `Error: ${detail}`);
                    renderMessages();
                    return;
                }
                await consumeSseStream(resp);
                // Network done, but the typewriter may still be catching up.
                await awaitStreamTyping();
            } catch (err) {
                stopStreamTyping();
                streamQueue = '';
                pendingDone = null;
                streamingText = '';
                liveTools.length = 0;
                pendingWrites = [];
                errors.push(`Network error: ${err.message || String(err)}`);
                renderMessages();
            } finally {
                inFlight = false;
                setSpinner(false);
                focusInput();
            }
        }

        // The page the user is on (the server names it from the URL; Settings
        // tabs live in the #fragment), plus the entity on a detail page.
        function readPageContext() {
            const loc = window.location;
            const ctx = { page: loc.pathname + loc.search + loc.hash };
            const body = document.body;
            const type = body && body.dataset.pageContextType;
            const id = body && body.dataset.pageContextId;
            if (type && id) {
                const parsed = parseInt(id, 10);
                if (!isNaN(parsed)) Object.assign(ctx, { type, id: parsed });
            }
            return ctx;
        }

        async function sendChatMessage() {
            if (inFlight) return;
            if (pendingWrites.length) {
                // The turn waits on its cards; say so rather than drop the
                // message, which stays in the box to send once they're answered.
                if (errors[errors.length - 1] !== ANSWER_CARDS_FIRST) errors.push(ANSWER_CARDS_FIRST);
                renderMessages();
                return;
            }
            if (!ctrl.hasInputTarget) return;
            const input = ctrl.inputTarget;
            const text = input.value.trim();
            if (!text) return;
            cancelTyping(true);
            errors.length = 0;
            messages.push({ role: 'user', content: text });
            input.value = '';
            ctrl.autosize();
            const payload = { messages, history };
            const ctx = readPageContext();
            if (ctx) payload.page_context = ctx;
            await postChat(payload);
        }

        function askChip(text) {
            if (!text) return;
            if (ctrl.hasInputTarget) ctrl.inputTarget.value = text;
            sendChatMessage();
        }

        function approveWrite(callId) { return decideWrite(callId, 'approve'); }
        function declineWrite(callId) { return decideWrite(callId, 'decline'); }

        // The server resumes a turn only with an answer for every card it
        // raised, so each answer is held until the last card is answered and
        // then all of them go in one request.
        async function decideWrite(callId, decision) {
            if (inFlight || !callId) return;
            if (!pendingWrites.some(p => p.id === callId)) return;
            writeDecisions.set(callId, decision);
            if (pendingWrites.some(p => !writeDecisions.has(p.id))) {
                renderMessages();
                return;
            }
            const answer = id => writeDecisions.get(id);
            const payload = {
                messages, history,
                approved_tool_call_ids: pendingWrites.filter(p => answer(p.id) === 'approve').map(p => p.id),
                declined_tool_call_ids: pendingWrites.filter(p => answer(p.id) === 'decline').map(p => p.id),
                page_context: readPageContext(),
            };
            cancelTyping(true);
            pendingWrites = [];
            writeDecisions.clear();
            const notice = errors.indexOf(ANSWER_CARDS_FIRST);
            if (notice !== -1) errors.splice(notice, 1);
            await postChat(payload);
        }

        // preventScroll: on open the phone sheet starts its slide-in below the
        // screen, and a plain focus() scrolls the page to reveal the input,
        // jolting the sheet mid-slide.
        function focusInput() {
            if (ctrl.hasInputTarget) ctrl.inputTarget.focus({ preventScroll: true });
        }

        // Reset to a fresh conversation: clears the transcript, history blob,
        // errors, and all streaming/typewriter state. Memory-only widget, so
        // there's nothing persisted to wipe. Ignored mid-turn so it can't strand
        // a running stream.
        function resetChat() {
            if (inFlight) return;
            stopStreamTyping();
            cancelTyping(false);
            messages.length = 0;
            errors.length = 0;
            pendingWrites = [];
            writeDecisions.clear();
            history = '';
            streamingText = '';
            streamQueue = '';
            pendingDone = null;
            lastShownAssistantContent = '';
            renderMessages();
            focusInput();
        }

        // "Ask the founders" in a reply: open the Support tab with the user's
        // last question typed in, for them to send or edit.
        function askFounders() {
            const last = [...messages].reverse().find(m => m.role === 'user');
            if (window.openSupport) window.openSupport(last ? String(last.content) : '');
        }

        return {
            sendChatMessage, resetChat, approveWrite,
            declineWrite, askChip, focusInput, askFounders,
        };
    }
